import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, Paperclip, Calendar, AlertCircle } from 'lucide-react'

const priorityConfig = {
  1: { label: 'Low', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  2: { label: 'Medium', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
  3: { label: 'High', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  4: { label: 'Critical', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
}

const typeLabels = {
  1: 'Bug',
  2: 'Feature',
  3: 'Improvement',
  4: 'Maintenance',
  5: 'Investigation',
  6: 'Other',
}

const typeColors = {
  1: 'text-red-600 bg-red-50',
  2: 'text-purple-600 bg-purple-50',
  3: 'text-amber-600 bg-amber-50',
  4: 'text-gray-600 bg-gray-100',
  5: 'text-blue-600 bg-blue-50',
  6: 'text-gray-500 bg-gray-50',
}

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-500', headerBg: 'bg-blue-50', headerBorder: 'border-blue-200', status: 1 },
  inProgress: { label: 'In Progress', color: 'bg-primary-500', headerBg: 'bg-orange-50', headerBorder: 'border-orange-200', status: 2 },
  onHold: { label: 'On Hold', color: 'bg-yellow-500', headerBg: 'bg-yellow-50', headerBorder: 'border-yellow-200', status: 3 },
  resolved: { label: 'Resolved', color: 'bg-green-500', headerBg: 'bg-green-50', headerBorder: 'border-green-200', status: 4 },
  closed: { label: 'Closed', color: 'bg-gray-400', headerBg: 'bg-gray-50', headerBorder: 'border-gray-200', status: 5 },
}

const scopeFlags = (scope) => {
  const parts = []
  if (scope & 1) parts.push('API')
  if (scope & 2) parts.push('Mobile')
  if (scope & 4) parts.push('Portal')
  return parts
}

const formatDate = (dateString) => {
  if (!dateString) return null
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const KanbanCard = ({ task, draggingId, onDragStart, onDragEnd }) => {
  const navigate = useNavigate()
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 4 && task.status !== 5
  const isDragging = draggingId === task.supportTaskId
  const priority = priorityConfig[task.priority] || priorityConfig[1]
  const scopes = scopeFlags(task.projectScope)

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({
          taskId: task.supportTaskId,
          currentStatus: task.status,
        }))
        e.dataTransfer.effectAllowed = 'move'
        onDragStart(task.supportTaskId)
      }}
      onDragEnd={onDragEnd}
      onClick={() => navigate(`/tasks/${task.supportTaskId}`)}
      className={`bg-white rounded-xl border border-gray-200/80 p-3.5 cursor-grab active:cursor-grabbing hover:shadow-lg hover:border-gray-300 transition-all select-none group ${
        isDragging ? 'opacity-40 scale-95 shadow-xl rotate-1' : 'shadow-sm'
      } ${isOverdue ? 'border-l-[3px] border-l-red-400' : ''}`}
    >
      {/* Top row: task number + priority */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-mono text-gray-400 tracking-wide">{task.taskNumber}</span>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${priority.bg} ${priority.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
          {priority.label}
        </span>
      </div>

      {/* Title */}
      <h4 className="text-[13px] font-semibold text-gray-900 line-clamp-2 mb-2.5 leading-snug group-hover:text-primary-700 transition-colors">
        {task.title}
      </h4>

      {/* Tags row: type + scope */}
      <div className="flex flex-wrap gap-1 mb-3">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${typeColors[task.taskType] || typeColors[6]}`}>
          {typeLabels[task.taskType] || 'Other'}
        </span>
        {scopes.map(s => (
          <span key={s} className="px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-500 bg-gray-100">
            {s}
          </span>
        ))}
      </div>

      {/* Footer: assignee, date, counts */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          {task.assignedToName ? (
            <span
              className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 shadow-sm"
              title={task.assignedToName}
            >
              {task.assignedToName[0].toUpperCase()}
            </span>
          ) : (
            <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-[10px] flex-shrink-0" title="Unassigned">
              ?
            </span>
          )}
          {task.assignedToName && (
            <span className="text-[11px] text-gray-500 truncate max-w-[80px]">{task.assignedToName.split(' ')[0]}</span>
          )}
        </div>
        <div className="flex items-center gap-2.5 text-gray-400">
          {(task.commentCount > 0) && (
            <span className="flex items-center gap-0.5 text-[10px]" title={`${task.commentCount} comments`}>
              <MessageSquare size={11} />
              <span>{task.commentCount}</span>
            </span>
          )}
          {(task.attachmentCount > 0) && (
            <span className="flex items-center gap-0.5 text-[10px]" title={`${task.attachmentCount} attachments`}>
              <Paperclip size={11} />
              <span>{task.attachmentCount}</span>
            </span>
          )}
          {task.dueDate && (
            <span className={`flex items-center gap-0.5 text-[10px] ${isOverdue ? 'text-red-500 font-semibold' : ''}`} title={isOverdue ? 'Overdue!' : `Due ${formatDate(task.dueDate)}`}>
              {isOverdue ? <AlertCircle size={11} /> : <Calendar size={11} />}
              <span>{formatDate(task.dueDate)}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const KanbanColumn = ({ title, tasks, color, headerBg, headerBorder, status, draggingId, dropTargetStatus, onDragStart, onDragEnd, onDrop, onDragOver, onDragLeave }) => {
  const dragCounterRef = useRef(0)

  const handleDragEnter = (e) => {
    e.preventDefault()
    dragCounterRef.current++
    onDragOver(status)
  }

  const handleDragLeave = () => {
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      onDragLeave()
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    dragCounterRef.current = 0
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'))
      if (data.currentStatus !== status) {
        onDrop(data.taskId, status)
      }
    } catch { /* ignore */ }
    onDragLeave()
  }

  const isDropTarget = dropTargetStatus === status && draggingId

  return (
    <div className="w-[300px] flex-shrink-0 flex flex-col">
      {/* Column header */}
      <div className={`flex items-center justify-between px-3 py-2.5 rounded-xl border ${headerBg} ${headerBorder} mb-3`}>
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${color} shadow-sm`} />
          <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        </div>
        <span className="text-xs font-bold text-gray-500 bg-white/70 px-2 py-0.5 rounded-full min-w-[24px] text-center shadow-sm">
          {tasks.length}
        </span>
      </div>

      {/* Column body */}
      <div
        onDragOver={(e) => { e.preventDefault(); handleDragEnter(e); }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex-1 rounded-xl p-2 space-y-2.5 min-h-[300px] max-h-[calc(100vh-300px)] overflow-y-auto transition-all ${
          isDropTarget
            ? 'ring-2 ring-primary-400 bg-primary-50/60 ring-offset-2'
            : 'bg-gray-50/80'
        }`}
      >
        {tasks.length === 0 ? (
          <div className={`flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed transition-all ${
            isDropTarget
              ? 'border-primary-300 text-primary-500 bg-primary-50/50'
              : 'border-gray-200 text-gray-400'
          }`}>
            <span className="text-xs font-medium">{isDropTarget ? 'Drop here' : 'No tasks'}</span>
          </div>
        ) : (
          tasks.map((task) => (
            <KanbanCard
              key={task.supportTaskId}
              task={task}
              draggingId={draggingId}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))
        )}
      </div>
    </div>
  )
}

const KanbanBoard = ({ data, onStatusChange, hideClosed }) => {
  const [draggingId, setDraggingId] = useState(null)
  const [dropTargetStatus, setDropTargetStatus] = useState(null)

  const handleDragStart = useCallback((taskId) => setDraggingId(taskId), [])
  const handleDragEnd = useCallback(() => { setDraggingId(null); setDropTargetStatus(null) }, [])
  const handleDragOver = useCallback((status) => setDropTargetStatus(status), [])
  const handleDragLeave = useCallback(() => setDropTargetStatus(null), [])

  const handleDrop = useCallback((taskId, newStatus) => {
    setDraggingId(null)
    setDropTargetStatus(null)
    onStatusChange?.(taskId, newStatus)
  }, [onStatusChange])

  if (!data) return null

  const allColumns = [
    { key: 'open', tasks: data.open || [], ...statusConfig.open },
    { key: 'inProgress', tasks: data.inProgress || [], ...statusConfig.inProgress },
    { key: 'onHold', tasks: data.onHold || [], ...statusConfig.onHold },
    { key: 'resolved', tasks: data.resolved || [], ...statusConfig.resolved },
    { key: 'closed', tasks: data.closed || [], ...statusConfig.closed },
  ]

  const columns = hideClosed ? allColumns.filter(col => col.key !== 'closed') : allColumns

  return (
    <div className="flex gap-5 overflow-x-auto pb-4 -mx-2 px-2">
      {columns.map((col) => (
        <KanbanColumn
          key={col.key}
          title={col.label}
          tasks={col.tasks}
          color={col.color}
          headerBg={col.headerBg}
          headerBorder={col.headerBorder}
          status={col.status}
          draggingId={draggingId}
          dropTargetStatus={dropTargetStatus}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        />
      ))}
    </div>
  )
}

export default KanbanBoard
